import { useEffect, useRef, useState } from "react";
import "../styles/calendar.css";
import "../styles/tooltip.css";

import { AiFillInfoCircle } from "react-icons/ai";
import {
  IonCard,
  IonCardContent,
  IonChip,
  IonIcon,
  IonPopover,
} from "@ionic/react";
import { informationCircleOutline } from "ionicons/icons";

import { t } from "../../translations";
import { Aa } from "../functions/Aa";
import { getNext20Days } from "../functions/dateFunctions";
import { shouldUseIonicComponents } from "../../utils/platformUtils";
import { hapticPatterns } from "../../utils/haptics";

const Calendar = (props) => {
  const [, setIndex] = useState(0);
  const [arrDays, setArrDays] = useState([]);
  const [, setYear] = useState("");
  const [, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [showPopover, setShowPopover] = useState(false);

  const listRef = useRef([]);

  const { lang } = props;
  const useMobileUI = shouldUseIonicComponents();

  useEffect(() => {
    const calculateCurrentMonthAndSetDays = async () => {
      const today = new Date();

      let year = today.getFullYear();

      let month = today.getMonth() + 1;
      let currentDay = today.getDate();

      month = month < 10 ? `0${month}` : month;

      currentDay = currentDay < 10 ? `0${currentDay}` : currentDay;

      setYear(year);
      setMonth(month);
      setDay(currentDay);

      setIndex(month - 1);
      const next20days = await getNext20Days(props?.allowToday || false);
      setArrDays(next20days);
    };

    calculateCurrentMonthAndSetDays();

    if (listRef.current[parseInt(day)]) {
      listRef.current[parseInt(day)].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start"
      });
    }
  }, [day, props?.allowToday]);

  // Mobile version with Ionic
  if (useMobileUI) {
    return (
      <div style={{ margin: "16px 0" }}>
        <IonCard>
          <IonCardContent>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px"
              }}
            >
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>
                {props?.title}
              </h3>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ fontSize: "14px", color: "#666" }}>
                  {t(`common.why_question`, lang)}
                </span>
                <IonIcon
                  id="calendar-info-trigger"
                  icon={informationCircleOutline}
                  style={{ fontSize: "20px", color: "#666", cursor: "pointer" }}
                  onClick={() => setShowPopover(true)}
                />
                <IonPopover
                  trigger="calendar-info-trigger"
                  isOpen={showPopover}
                  onDidDismiss={() => setShowPopover(false)}
                  side="top"
                  alignment="center"
                >
                  <div style={{ padding: "12px", maxWidth: "250px" }}>
                    <p style={{ margin: 0, fontSize: "13px" }}>
                      {t(`common.why_question_tooltip`, lang)}
                    </p>
                  </div>
                </IonPopover>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                overflowX: "auto",
                gap: "8px",
                paddingBottom: "8px",
                scrollBehavior: "smooth",
                WebkitOverflowScrolling: "touch"
              }}
            >
              {arrDays.map((item, count) => (
                <IonChip
                  key={item?.dayLong}
                  ref={(el) => (listRef.current[count] = el)}
                  color={props?.selectedDate === item?.dayLong ? "primary" : ""}
                  outline={props?.selectedDate !== item?.dayLong}
                  onClick={async () => {
                    await hapticPatterns.selection();
                    props?.setDay(item?.dayLong);

                    if (listRef.current[count]) {
                      listRef.current[count].scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                        inline: "center"
                      });
                    }
                  }}
                  style={{
                    minWidth: "80px",
                    height: "auto",
                    padding: "8px 12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    flexShrink: 0
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "2px"
                    }}
                  >
                    <span style={{ fontWeight: "600", fontSize: "14px" }}>
                      {Aa(
                        props?.lang === "en"
                          ? item?.dayTextEnglish
                          : item?.dayTextFrench
                      )}
                    </span>
                    <span style={{ fontSize: "12px" }}>
                      {Aa(
                        props?.lang === "en"
                          ? item?.monthTextEnglish
                          : item?.monthTextFrench
                      )}{" "}
                      {item?.day}
                    </span>
                  </div>
                </IonChip>
              ))}
            </div>
          </IonCardContent>
        </IonCard>
      </div>
    );
  }

  // Web version (original)
  return (
    <div className="calendar-bar">
      <div className="calendar-block">
        <p
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5
          }}
        >
          {props?.title}
          <span
            className="tooltip-container"
            style={{
              color: "#838383",
              display: "flex",
              alignItems: "center",
              gap: 5,
              cursor: "help"
            }}
          >
            {" "}
            {t(`common.why_question`, lang)}
            <AiFillInfoCircle size={20} />
            <div className="tooltip-popup">
              {t(`common.why_question_tooltip`, lang)}
              <div className="tooltip-arrow" />
            </div>
          </span>
        </p>
        <div className="calendar-box">
          <div className="calendar-days" style={{}}>
            {arrDays.map((item, count) => (
              <div
                key={item?.dayLong}
                className="btn-days-calendar"
                ref={(el) => (listRef.current[count] = el)}
                style={{
                  border:
                    props?.selectedDate === item?.dayLong
                      ? "none"
                      : "1px solid #dedede",
                  color:
                    props?.selectedDate === item?.dayLong ? "#fff" : "#040404",
                  backgroundColor:
                    props?.selectedDate === item?.dayLong ? "#FF114D" : "#fff"
                }}
                onClick={() => {
                  props?.setDay(item?.dayLong);

                  listRef.current[count].scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "center"
                  });
                }}
              >
                <span
                  style={{
                    fontWeight: "600"
                  }}
                >
                  {Aa(
                    props?.lang === "en"
                      ? item?.dayTextEnglish
                      : item?.dayTextFrench
                  )}
                </span>

                <span
                  style={{
                    fontSize: 14,
                    fontWeight: "400"
                  }}
                >
                  {Aa(
                    props?.lang === "en"
                      ? item?.monthTextEnglish
                      : item?.monthTextFrench
                  )}{" "}
                  {item?.day}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
